<template>
  <div class="payment-return-page">
    <div class="container p-6 mx-auto max-w-lg">
      <div class="p-8 text-center bg-white rounded-lg shadow-md">
        <div v-if="loading" class="flex flex-col justify-center items-center py-8">
          <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
          <p class="mt-4 text-gray-600">Processing your payment...</p>
        </div>

        <div v-else-if="error" class="py-8 text-center">
          <i class="mb-4 text-6xl text-red-500 pi pi-times-circle"></i>
          <h2 class="mb-2 text-2xl font-bold text-gray-800">Payment Failed</h2>
          <p class="mb-6 text-gray-600">{{ errorMessage }}</p>
          <Button label="Try Again" @click="goToCheckout" class="mr-2" />
          <Button label="Go to Dashboard" @click="goToDashboard" severity="secondary" />
        </div>

        <div v-else class="py-8 text-center">
          <i class="mb-4 text-6xl text-green-500 pi pi-check-circle"></i>
          <h2 class="mb-2 text-2xl font-bold text-gray-800">Payment Successful!</h2>
          <p class="mb-6 text-gray-600">Your payment has been processed successfully.</p>
          <Button label="View Order" @click="viewOrder" class="mr-2" v-if="orderId" />
          <Button label="Go to Dashboard" @click="goToDashboard" severity="secondary" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import { clearPaymentState, getPaymentState } from '@/utils/paymentReturnHandler';

const router = useRouter();
const toast = useToast();

const loading = ref(true);
const success = ref(false);
const error = ref(false);
const errorMessage = ref('');
const orderId = ref(null);
const returnPath = ref('/');

// Fix viewport issues on payment return
const fixViewportIssues = () => {
  // Force viewport recalculation
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);

  // Scroll to top to prevent white space
  window.scrollTo(0, 0);

  // Force body to take full height
  document.body.style.height = '100vh';
  document.body.style.height = `calc(var(--vh, 1vh) * 100)`;

  // Prevent overscroll
  document.body.style.overscrollBehavior = 'contain';
};

// Process payment return
onMounted(async () => {
  // Fix viewport issues immediately
  fixViewportIssues();

  // Fix viewport again after a short delay
  setTimeout(fixViewportIssues, 100);

  try {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('reference') ||
                     urlParams.get('trxref') ||
                     urlParams.get('transaction_id') ||
                     urlParams.get('tx_ref') ||
                     urlParams.get('payment_intent');

    // Get payment state
    const paymentState = getPaymentState();

    if (paymentState) {
      orderId.value = paymentState.orderId;
      returnPath.value = paymentState.route || '/';
    }

    // Determine if payment was successful based on URL parameters
    // In a real app, you would verify with your backend
    const isSuccessful = urlParams.has('status') ?
                        ['success', 'successful', 'approved'].includes(urlParams.get('status').toLowerCase()) :
                        !urlParams.has('canceled') && !urlParams.has('failed');

    // Simulate API call to verify payment
    await new Promise(resolve => setTimeout(resolve, 1500));

    if (isSuccessful) {
      success.value = true;
      toast.add({
        severity: 'success',
        summary: 'Payment Successful',
        detail: 'Your payment has been processed successfully',
        life: 5000
      });

      // In a real app, you would call your backend to verify the payment
      // const response = await axios.post('/api/verify-payment', { reference });
      // if (response.data.success) {
      //   success.value = true;
      // } else {
      //   throw new Error(response.data.message || 'Payment verification failed');
      // }
    } else {
      throw new Error('Payment was not successful');
    }
  } catch (err) {
    error.value = true;
    errorMessage.value = err.message || 'There was an issue processing your payment';
    toast.add({
      severity: 'error',
      summary: 'Payment Failed',
      detail: errorMessage.value,
      life: 5000
    });
  } finally {
    loading.value = false;

    // Clear payment state
    clearPaymentState();

    // Auto-redirect after successful payment
    if (success.value) {
      setTimeout(() => {
        goToDashboard();
      }, 3000);
    }
  }
});

// Navigation functions
const goToDashboard = () => {
  router.replace('/');
};

const goToCheckout = () => {
  router.replace('/checkout');
};

const viewOrder = () => {
  if (orderId.value) {
    router.replace(`/orders/${orderId.value}`);
  } else {
    router.replace('/orders');
  }
};
</script>

<style scoped>
.payment-return-page {
  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* Prevent white space on mobile */
@media screen and (max-width: 768px) {
  .payment-return-page {
    height: 100vh;
    height: calc(var(--vh, 1vh) * 100);
    overflow-y: auto;
  }
}
</style>
